import { useRef, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, MenuList } from '@mui/material';

import { ScanActionItem, ScanActionType } from '../types/Scan';
import { ButtonState } from './ScanOrBoxInfoHeader';

const CONTEXT_MENU_WIDTH = 230;

export interface Props {
    items: Array<ScanActionItem>;
    onAction: (type: ScanActionType) => void;
    openMenuBtnText?: string;
    buttonState: ButtonState;
    customStyles?: object;
    /** Menu width in pixels. Defaults to button width if not specified */
    menuWidth?: number;
    /** Alignment relative to button. Defaults to 'right' */
    align?: 'left' | 'right';
}

const ContextMenu = ({
    items,
    onAction,
    openMenuBtnText = 'Actions',
    buttonState,
    customStyles,
    menuWidth,
    align = 'left'
}: Props) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);

    const handleClick = () => {
        if (buttonRef?.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const width = menuWidth ?? CONTEXT_MENU_WIDTH;

            setMenuPosition({
                top: rect.bottom + 10,
                left: align === 'right' ? rect.right - width : rect.left,
                width
            });
        }
    };

    const handleClose = () => {
        setMenuPosition(null);
    };

    const handleAction = (type: ScanActionType) => {
        onAction(type);
        handleClose();
    };

    return (
        <>
            <Button
                ref={buttonRef}
                disabled={buttonState.isDisabled}
                onClick={handleClick}
                endIcon={<ExpandMoreIcon />}
                variant="outlined"
            >
                {openMenuBtnText}
            </Button>
            <Menu
                open={Boolean(menuPosition)}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={menuPosition ?? { top: 0, left: 0 }}
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: '#f8f8f8',
                            borderRadius: '6px',
                            width: menuWidth ?? CONTEXT_MENU_WIDTH,
                            maxWidth: '100%',
                            ...customStyles
                        }
                    }
                }}
            >
                <MenuList sx={{ paddingTop: '0', paddingBottom: '0' }}>
                    {items.map((item) => (
                        <MenuItem key={item.type} onClick={() => handleAction(item.type)}>
                            <ListItemIcon>
                                <item.icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{item.text}</ListItemText>
                        </MenuItem>
                    ))}
                </MenuList>
            </Menu>
        </>
    );
};

export default ContextMenu;
